/**
 * Connector detail page - per-installed-connector administration.
 *
 * Tabs: Overview, Config, Events, Inbound, Health, Executions.
 * Header: title + status badge + Test/Activate/Pause/Disable/Delete buttons.
 *
 * Backend contract: see /v1/foundational/integrations/connectors/:id (+ /test,
 * /activate, /pause, /disable, /oauth/initiate).
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Pause,
  Play,
  PowerOff,
  RefreshCw,
  Settings,
  Shield,
  Trash2,
  Webhook,
  Zap,
} from 'lucide-react';

import {
  type ConnectorInstance,
  type ConnectorTemplate,
  type DispatchOutcome,
  deleteConnector,
  getConnector,
  getTemplate,
  testConnector,
  transitionConnector,
  updateConnector,
} from '@/lib/marketplace-api';
import { GlassCard, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

type Tab = 'overview' | 'config' | 'events' | 'inbound' | 'health';

export function ConnectorDetailPage(): JSX.Element {
  const { connectorId } = useParams({ from: '/admin/integrations/connector/$connectorId' });
  const [connector, setConnector] = useState<ConnectorInstance | null>(null);
  const [template, setTemplate] = useState<ConnectorTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<DispatchOutcome | null>(null);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorId]);

  async function load(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      const c = await getConnector(connectorId);
      setConnector(c);
      const t = await getTemplate(c.templatePlatformKey);
      setTemplate(t);
    } catch (err) {
      setLoadError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(
    label: string,
    fn: () => Promise<unknown>,
  ): Promise<void> {
    setActionPending(label);
    setActionError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setActionError(String((err as Error)?.message ?? err));
    } finally {
      setActionPending(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading connector…
      </div>
    );
  }

  if (loadError || !connector || !template) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-8">
        <Link to="/admin/integrations" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Marketplace
        </Link>
        <div className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          <AlertTriangle className="mb-2 h-5 w-5" />
          {loadError ?? 'Connector not found'}
        </div>
      </div>
    );
  }

  const isOauth = template.authType === 'oauth2';

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      <Link to="/admin/integrations" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Marketplace
      </Link>

      {/* Header */}
      <header className="mt-4 mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-2xl font-semibold text-foreground">{connector.displayName}</h1>
            <Badge variant="outline" className="text-[10px] uppercase">{template.tier}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {template.platformKey} · {template.category}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={actionPending !== null}
            onClick={() => void handleAction('test', async () => {
              const r = await testConnector(connector.id);
              setTestResult(r);
            })}
          >
            {actionPending === 'test' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
            Test
          </Button>

          {connector.status === 'active' && (
            <Button size="sm" variant="outline" disabled={actionPending !== null} onClick={() => void handleAction('pause', () => transitionConnector(connector.id, 'pause'))}>
              <Pause className="mr-2 h-4 w-4" /> Pause
            </Button>
          )}
          {(connector.status === 'paused' || connector.status === 'testing' || connector.status === 'error') && (
            <Button size="sm" disabled={actionPending !== null} onClick={() => void handleAction('activate', () => transitionConnector(connector.id, 'activate'))}>
              <Play className="mr-2 h-4 w-4" /> Activate
            </Button>
          )}
          {connector.status === 'draft' && (
            <Button size="sm" disabled={actionPending !== null} onClick={() => void handleAction('activate', () => transitionConnector(connector.id, 'activate'))}>
              <Play className="mr-2 h-4 w-4" /> Activate (via testing)
            </Button>
          )}
          {connector.status !== 'disabled' && (
            <Button size="sm" variant="outline" disabled={actionPending !== null} onClick={() => void handleAction('disable', () => transitionConnector(connector.id, 'disable'))}>
              <PowerOff className="mr-2 h-4 w-4" /> Disable
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={actionPending !== null}
            onClick={() => {
              if (confirm(`Permanently delete "${connector.displayName}"? This cannot be undone.`)) {
                void handleAction('delete', async () => {
                  await deleteConnector(connector.id);
                  window.location.href = '/admin/integrations';
                });
              }
            }}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        </div>
      </header>

      {/* Status strip */}
      <GlassCard className="mb-6 flex flex-wrap items-center justify-between gap-4 p-4">
        <div className="flex items-center gap-6">
          <StatusBlock label="Status" value={connector.status} tone={statusTone(connector.status)} />
          <StatusBlock label="Health" value={connector.healthStatus ?? 'unknown'} tone={healthTone(connector.healthStatus)} />
          <StatusBlock label="Auth" value={template.authType} tone="default" />
          {connector.consecutiveFailures > 0 && (
            <StatusBlock label="Consecutive failures" value={String(connector.consecutiveFailures)} tone="red" />
          )}
          {connector.lastSuccessAt && (
            <StatusBlock
              label="Last success"
              value={new Date(connector.lastSuccessAt).toLocaleString()}
              tone="emerald"
            />
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => void load()}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </GlassCard>

      {actionError && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="-mt-0.5 mr-1 inline h-4 w-4" />
          {actionError}
        </div>
      )}

      {testResult && (
        <GlassCard className="mb-4 border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="flex items-start gap-3">
            {testResult.status === 'success' ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-500" />
            ) : (
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-500" />
            )}
            <div className="flex-1">
              <div className="font-medium text-foreground">
                Test {testResult.status === 'success' ? 'passed' : 'failed'}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                HTTP {testResult.httpStatus ?? '-'} · {testResult.durationMs} ms · attempt {testResult.attemptNumber}
              </div>
              {testResult.errorMessage && (
                <div className="mt-2 rounded bg-background/60 p-2 font-mono text-xs text-destructive">
                  {testResult.errorMessage}
                </div>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setTestResult(null)}>Dismiss</Button>
          </div>
        </GlassCard>
      )}

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-2 border-b border-border">
        {(['overview', 'config', 'events', 'inbound', 'health'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveTab(t)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium capitalize transition',
              activeTab === t
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab connector={connector} template={template} />}
      {activeTab === 'config' && <ConfigTab connector={connector} template={template} onSaved={() => void load()} />}
      {activeTab === 'events' && <EventsTab connector={connector} template={template} onSaved={() => void load()} />}
      {activeTab === 'inbound' && <InboundTab connector={connector} template={template} />}
      {activeTab === 'health' && <HealthTab connector={connector} />}

      {isOauth && (
        <GlassCard className="mt-6 p-4">
          <CardHeader className="p-0">
            <CardTitle className="text-base">OAuth connection</CardTitle>
            <CardDescription>
              This template requires OAuth2. Connecting will redirect you to {template.displayName} to grant access; tokens are then encrypted and stored.
            </CardDescription>
          </CardHeader>
          <div className="mt-3">
            <Button size="sm" disabled>
              Connect via OAuth (callback flow - coming in P2.4 UI wiring)
            </Button>
          </div>
        </GlassCard>
      )}
    </div>
  );
}

function StatusBlock(props: { label: string; value: string; tone: 'emerald' | 'amber' | 'cyan' | 'red' | 'default' }): JSX.Element {
  const tone: Record<string, string> = {
    default: 'text-foreground',
    emerald: 'text-emerald-500',
    amber: 'text-amber-500',
    cyan: 'text-cyan-500',
    red: 'text-red-500',
  };
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{props.label}</div>
      <div className={cn('mt-1 font-mono text-sm capitalize', tone[props.tone])}>{props.value}</div>
    </div>
  );
}

function statusTone(s: ConnectorInstance['status']): 'emerald' | 'amber' | 'cyan' | 'red' | 'default' {
  if (s === 'active') return 'emerald';
  if (s === 'testing') return 'cyan';
  if (s === 'paused') return 'amber';
  if (s === 'error') return 'red';
  return 'default';
}

function healthTone(h: ConnectorInstance['healthStatus']): 'emerald' | 'amber' | 'cyan' | 'red' | 'default' {
  if (h === 'healthy') return 'emerald';
  if (h === 'degraded') return 'amber';
  if (h === 'failing') return 'red';
  return 'default';
}

// ---------------- Tabs ----------------

function OverviewTab(props: { connector: ConnectorInstance; template: ConnectorTemplate }): JSX.Element {
  const { connector, template } = props;
  return (
    <div className="space-y-4">
      <GlassCard className="p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Settings className="h-4 w-4" />
          Template
        </h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Platform key</dt>
            <dd className="font-mono">{template.platformKey}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Category</dt>
            <dd>{template.category}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Tier</dt>
            <dd className="capitalize">{template.tier}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Auth type</dt>
            <dd>{template.authType}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Description</dt>
            <dd>{template.description ?? '-'}</dd>
          </div>
        </dl>
      </GlassCard>

      <GlassCard className="p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Shield className="h-4 w-4" />
          Privacy + residency
        </h3>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">PII: {template.handlesPii ? 'Yes' : 'No'}</Badge>
          <Badge variant="outline">Risk: {template.privacyRiskLevel}</Badge>
          {template.dataResidency && <Badge variant="outline">Residency: {template.dataResidency}</Badge>}
          {template.isIndiaStack && <Badge variant="outline">India Stack</Badge>}
          {template.supportsErasure && <Badge variant="outline">Supports erasure</Badge>}
        </div>
        {connector.consecutiveFailures > 0 && (
          <p className="mt-3 text-xs text-amber-600">
            Connector has failed {connector.consecutiveFailures} consecutive deliveries. Circuit breaker trips at 10.
          </p>
        )}
      </GlassCard>
    </div>
  );
}

function ConfigTab(props: { connector: ConnectorInstance; template: ConnectorTemplate; onSaved: () => void }): JSX.Element {
  const schema = props.template.configSchema as {
    properties?: Record<string, { type?: string; format?: string; title?: string; description?: string; enum?: string[] }>;
    required?: string[];
  };
  const required = new Set(schema.required ?? []);
  const fields = Object.entries(schema.properties ?? {});

  const initial: Record<string, string> = {};
  for (const k of Object.keys(props.connector.config)) {
    const v = (props.connector.config as Record<string, unknown>)[k];
    initial[k] = typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v);
  }
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [displayName, setDisplayName] = useState(props.connector.displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await updateConnector(props.connector.id, { displayName, config: values });
      props.onSaved();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassCard className="p-5">
      <div className="mb-4 space-y-1.5">
        <label className="text-xs font-medium text-foreground/80">Display name</label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>
      <div className="space-y-3">
        {fields.map(([key, spec]) => {
          const isSecret = /(Token|Secret|Key|Password|Pwd|Pass)$/i.test(key) || spec.format === 'password';
          return (
            <div key={key} className="space-y-1.5">
              <label className="text-xs font-medium text-foreground/80">
                {spec.title ?? key}
                {required.has(key) && <span className="ml-1 text-red-500">*</span>}
                {isSecret && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">secret</span>}
              </label>
              {spec.description && <p className="text-[11px] text-muted-foreground">{spec.description}</p>}
              {spec.enum ? (
                <select
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select…</option>
                  {spec.enum.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input
                  type={isSecret ? 'password' : spec.format === 'uri' ? 'url' : 'text'}
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                  placeholder={isSecret ? 'Leave blank to keep existing' : `Enter ${key}`}
                />
              )}
            </div>
          );
        })}
      </div>
      {error && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div>}
      <div className="mt-4 flex justify-end">
        <Button onClick={() => void save()} disabled={saving} size="sm">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save
        </Button>
      </div>
    </GlassCard>
  );
}

function EventsTab(props: { connector: ConnectorInstance; template: ConnectorTemplate; onSaved: () => void }): JSX.Element {
  const [subscriptions, setSubscriptions] = useState<string[]>(props.connector.eventSubscriptions);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const all = props.template.supportedEvents;

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await updateConnector(props.connector.id, { eventSubscriptions: subscriptions });
      props.onSaved();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassCard className="p-5">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Outbound event subscriptions</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Pick which event types fire this connector. {all.length} available.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {all.map((ev) => (
          <button
            key={ev}
            type="button"
            onClick={() => {
              setSubscriptions((s) => (s.includes(ev) ? s.filter((x) => x !== ev) : [...s, ev]));
            }}
            className={cn(
              'rounded-full px-2.5 py-1 font-mono text-[10px] transition',
              subscriptions.includes(ev)
                ? 'bg-primary/15 text-primary'
                : 'bg-muted text-muted-foreground hover:bg-muted/70',
            )}
          >
            {ev}
          </button>
        ))}
      </div>
      {error && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div>}
      <div className="mt-4 flex justify-end">
        <Button onClick={() => void save()} disabled={saving} size="sm">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save
        </Button>
      </div>
    </GlassCard>
  );
}

function InboundTab(props: { connector: ConnectorInstance; template: ConnectorTemplate }): JSX.Element {
  const inboundUrl = `${typeof window !== 'undefined' ? window.location.origin : '<your-host>'}/v1/incoming/integrations/${props.connector.id}`;
  return (
    <GlassCard className="p-5 space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Webhook className="h-4 w-4" /> Inbound webhook
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {props.template.inboundEvents.length > 0
            ? `${props.template.displayName} supports ${props.template.inboundEvents.length} inbound event types: ${props.template.inboundEvents.join(', ')}.`
            : 'This connector does not declare inbound event types.'}
        </p>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-foreground/80">Webhook URL</div>
        <div className="rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs">
          {inboundUrl}
        </div>
      </div>

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
        <AlertTriangle className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
        Inbound delivery requires <span className="font-mono">inbound_enabled=true</span> and an
        <span className="font-mono"> inbound_secret</span> set on the connector. Set these via the
        Settings page (tier-4 UI work - until then, ops sets via direct DB UPDATE).
        Current status: <span className="font-mono">{props.connector.inboundEnabled ? 'ENABLED' : 'disabled'}</span>.
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-foreground/80">Required headers (Standard Webhooks spec)</div>
        <pre className="overflow-auto rounded-md border border-border bg-muted px-3 py-2 font-mono text-[11px]">
{`X-Foctta-Webhook-Id:         <unique-id-per-event>
X-Foctta-Webhook-Timestamp:  <unix-seconds>
X-Foctta-Webhook-Signature:  sha256=HEX(HMAC_SHA256(secret, \`\${ts}.\${rawBody}\`))
Content-Type:                application/json`}
        </pre>
        <p className="mt-1 text-[11px] text-muted-foreground">
          5-minute replay window. Webhook-Id dedup TTL 24h. Timing-safe signature compare.
        </p>
      </div>
    </GlassCard>
  );
}

function HealthTab(props: { connector: ConnectorInstance }): JSX.Element {
  return (
    <GlassCard className="p-5">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Activity className="h-4 w-4" /> Health
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Health status</dt>
          <dd className="capitalize">{props.connector.healthStatus ?? 'unknown'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Consecutive failures</dt>
          <dd>{props.connector.consecutiveFailures}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Last success</dt>
          <dd>{props.connector.lastSuccessAt ? new Date(props.connector.lastSuccessAt).toLocaleString() : '-'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Last failure</dt>
          <dd>{props.connector.lastFailureAt ? new Date(props.connector.lastFailureAt).toLocaleString() : '-'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Created</dt>
          <dd>{new Date(props.connector.createdAt).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Updated</dt>
          <dd>{new Date(props.connector.updatedAt).toLocaleString()}</dd>
        </div>
      </dl>
      <p className="mt-4 text-xs text-muted-foreground">
        Connector executions log (recent retries, errors, response bodies) - listing UI lands with P2.4.
      </p>
    </GlassCard>
  );
}
